import useEventListener from '../../hooks/useEventListener.hooks';
import { wasInterceptionPromptedFor } from '../../utils/agentInterception';

/**
 * HITL 3.0 intent fast-path: Alt+1…9 fire the intent with that shortcut number,
 * matching the on-pill badges. Numbering is contiguous over the keyboard-
 * reachable intents (Request Staff is excluded), so there is no gap.
 *
 * Alt (not a bare digit) is deliberate:
 *  - it never collides with typing in the Quick Response box, so the shortcut
 *    works even mid-message — no "is an input focused?" guard needed;
 *  - it doesn't fight Ctrl push-to-talk (`Speech/SpeechToText.ts`);
 *  - `preventDefault()` stops macOS Option+digit from inserting a glyph.
 *
 * `event.code` (Digit1…Digit9) is matched so it stays layout-independent and
 * unaffected by Alt remapping the produced character. Out-of-range digits are
 * no-ops — `fireIntentByShortcut` owns that guard.
 *
 * ## It does NOT stopPropagation, unlike the other Alt shortcuts
 *
 * Firing an intent speaks to the customer, so while the AI is driving it IS taking
 * the order over and must raise the take-over prompt — exactly as CLICKING the pill
 * does, since the pills carry no interception-ignored marker.
 *
 * `AgentInterceptionConfirm` listens on `document` in the capture phase, which runs
 * strictly after this window-capture listener, so a `stopPropagation()` here hides
 * the keystroke from it. The prompt used to appear anyway, as a side effect of the
 * BARE `Alt` keydown being treated as an interaction; once that was correctly
 * ignored (its dialog trapped focus and broke every Alt shortcut), Alt+1…9 fired
 * silently with no prompt at all.
 *
 * `preventDefault()` stays — that is what suppresses the macOS Option+digit glyph,
 * and it is independent of propagation. Sibling listeners on `window` were never
 * affected by the `stopPropagation` either, so nothing else relied on it.
 *
 * Contrast `useFlagIssueHotkey`, which DOES stop propagation on purpose: flagging a
 * defect is supervisory, not taking the order over.
 *
 * ## …and why firing is deferred to a timeout
 *
 * Propagating is necessary but not sufficient. Firing an intent dispatches
 * `sendAgentInterception`, which sets `isAIAutoMode` false — and under React 17 a
 * state update inside a NATIVE listener is not batched, so that re-renders
 * `AgentInterceptionConfirm` synchronously, right here in the window-capture phase.
 * Its effect deps change, its cleanup runs, and its document-capture listeners are
 * REMOVED before the event ever reaches them. The guard disarms itself with the very
 * action it exists to confirm.
 *
 * Captured in the browser — `DETACHED` lands between the fire and its return, and no
 * handler line for `Digit1` is ever logged:
 *
 *     [intent hotkey firing]      { code: 'Digit1', key: '¡' }
 *     [guard DETACHED]
 *     [guard effect]              { confirmAgentInterception: false }
 *     [intent hotkey dispatch returned]
 *
 * This is also why clicking the pill works: a mouse press is TWO events, and
 * `mousedown` reaches the guard while it is still armed, before the `click` that
 * fires the intent. The keyboard has one event doing both jobs.
 *
 * Deferring gives the keyboard that same ordering — the keydown finishes
 * propagating, the guard sees it armed, the intent dispatches immediately after.
 * `preventDefault()` stays synchronous because it has to; only the firing moves.
 *
 * `setTimeout`, NOT `queueMicrotask`. A microtask does not survive the event: the JS
 * stack empties as each listener returns, so the browser runs a microtask checkpoint
 * BETWEEN listeners and the callback fires before document capture is ever reached.
 * Tried it, and the prompt still did not appear. Only a macrotask clears the whole
 * dispatch.
 *
 * ## …and why it then checks for an open dialog
 *
 * Clicking a pill raises the prompt WITHOUT speaking to the customer, and that turns
 * out to be an accident rather than a design: the dialog opens on `mousedown`, so its
 * backdrop is in the DOM before `mouseup`, the pill never receives the `click`, and
 * the intent never fires. Verified in the app — the pill prompts and sends no TTS.
 *
 * A keydown has no such accident. Without this check Alt+1 would greet the customer
 * while simultaneously asking whether the agent meant to take over, which is the one
 * outcome nobody wants. Checking the DOM for an open dialog reproduces deliberately
 * what the mouse gets for free, and matches the guard `LiveTranscriptCard` already
 * uses for the same reason.
 *
 * Answering the prompt does not retroactively send the suppressed intent, either
 * way. `No` leaves the guard armed, so the next press prompts again. `Yes` latches
 * it, so the next press raises nothing, is not suppressed, and speaks. The agent
 * presses again — exactly as they must click the pill again today.
 *
 * NOT covered by a jsdom test: RTL's `fireEvent` wraps in `act()`, which batches the
 * update and hides the whole mechanism — with or without this deferral the test
 * passes either way. The evidence above is the browser, not the suite.
 *
 * HITL3-only: mounted from `IntentsSectionGrouped`, which renders solely in the
 * new layout.
 */
const useIntentHotkeys = (fireIntentByShortcut: (shortcut: number) => void) => {
  const handleKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return;
    }
    const match = /^Digit([1-9])$/.exec(e.code);
    if (!match) {
      return;
    }
    // preventDefault only, and synchronously — see the notes above.
    e.preventDefault();
    const shortcut = Number(match[1]);
    setTimeout(() => {
      // This very keystroke raised the take-over prompt — see the note above.
      if (wasInterceptionPromptedFor(e)) {
        return;
      }
      fireIntentByShortcut(shortcut);
    }, 0);
  };

  // Capture phase so the focused omnibar can't swallow the key first (and macOS
  // Option+digit never reaches the input to insert a glyph).
  useEventListener('keydown', handleKeyDown, window, true);
};

export default useIntentHotkeys;
